
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { getMedicalRecordById, deleteMedicalRecord } from '@/services/medicalService';
import { MedicalRecord } from '@/hooks/useMedicalRecords';
import { format } from 'date-fns';
import { Paperclip, Edit, ArrowLeft, Download, ExternalLink, Trash2 } from 'lucide-react';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';

export default function MedicalRecordDetail() {
  const { petId, recordId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [record, setRecord] = useState<MedicalRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (recordId) {
      setLoading(true);
      getMedicalRecordById(recordId)
        .then((data: MedicalRecord) => {
          console.log('Medical record data:', data); // Debug log
          setRecord(data);
        })
        .catch(err => {
          console.error('Error loading medical record:', err); // Debug log
          setError("Could not load medical record details");
          toast({
            title: "Error",
            description: "Failed to load medical record details",
            variant: "destructive"
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [recordId, toast]);

  // Get display text for record type
  const getRecordTypeDisplay = (type: string) => {
    const typeMap: Record<string, string> = {
      vaccine_record: 'Vaccine Record',
      diagnosis: 'Diagnosis',
      treatment: 'Treatment',
      surgery: 'Surgery',
      medication_log: 'Medication',
      lab_result: 'Lab Result',
      general_note: 'Note'
    };
    
    return typeMap[type] || 'Record';
  };

  // Get badge variant for record type
  const getRecordTypeBadge = (type: string) => {
    const variants: Record<string, { className: string }> = {
      vaccine_record: { className: 'bg-blue-50 text-blue-600 border-blue-200' },
      diagnosis: { className: 'bg-amber-50 text-amber-600 border-amber-200' },
      treatment: { className: 'bg-green-50 text-green-600 border-green-200' },
      surgery: { className: 'bg-purple-50 text-purple-600 border-purple-200' },
      medication_log: { className: 'bg-pink-50 text-pink-600 border-pink-200' },
      lab_result: { className: 'bg-cyan-50 text-cyan-600 border-cyan-200' },
      general_note: { className: 'bg-gray-50 text-gray-600 border-gray-200' }
    };
    
    return variants[type] || { className: 'bg-gray-50 text-gray-600 border-gray-200' };
  };

  const handleDownloadAttachment = (url: string, index: number) => {
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = `medical-record-attachment-${index + 1}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast({
        title: "Download Error",
        description: "Could not download the attachment",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async () => {
    if (!recordId) return;
    
    setIsDeleting(true);
    try {
      await deleteMedicalRecord(recordId);
      toast({
        title: "Record deleted",
        description: "Medical record has been successfully deleted.",
      });
      navigate(`/medical/${petId}/records`);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete medical record. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pet-purple mx-auto mb-4"></div>
          <p className="text-gray-600">Loading medical record...</p>
        </div>
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="p-4 text-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
          <p className="text-red-600 mb-4">
            {error || "Medical record not found"}
          </p>
          <Button 
            onClick={() => navigate(`/medical/${petId}/records`)}
            variant="outline"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Medical Records
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6 max-w-4xl mx-auto">
      {/* Header with back button and edit button */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={() => navigate(`/medical/${petId}/records`)}
          className="p-0 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Records
        </Button>
        
        <div className="flex gap-2">
          <Button
            onClick={() => navigate(`/medical/${petId}/records/${recordId}/edit`)}
            className="bg-pet-purple hover:bg-pet-purple/90"
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Record
          </Button>
          <Button
            variant="destructive"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Main record details card */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <CardTitle className="text-xl mb-2">{record.title}</CardTitle>
              <p className="text-sm text-gray-500">
                {format(new Date(record.record_date), 'PPP')}
              </p>
              {record.vet_name && (
                <p className="text-sm text-gray-600 mt-1">
                  By: {record.vet_name}
                </p>
              )}
            </div>
            <Badge 
              variant="outline" 
              className={getRecordTypeBadge(record.record_type).className}
            >
              {getRecordTypeDisplay(record.record_type)}
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Description */}
          {record.description && (
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-2">Description</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-900 whitespace-pre-wrap">{record.description}</p>
              </div>
            </div>
          )}

          {/* Attachments */}
          {record.has_attachments && (
            <div>
              <h3 className="font-semibold text-sm text-gray-700 mb-3 flex items-center">
                <Paperclip className="h-4 w-4 mr-1" />
                Attachments
              </h3>
              
              {record.attachments && record.attachments.length > 0 ? (
                <div className="space-y-3">
                  {record.attachments.map((url, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <Paperclip className="h-4 w-4 text-gray-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Medical Document {index + 1}
                          </p>
                          <p className="text-xs text-gray-500">
                            Lab results, prescription, or medical document
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(url, '_blank')}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadAttachment(url, index)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Paperclip className="h-4 w-4 text-amber-600" />
                    <p className="text-sm text-amber-800">
                      This record indicates it has attachments, but they may not be properly linked. 
                      You can edit this record to re-upload any missing files.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Record metadata */}
          <div className="pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-500">
              <div>
                <p className="font-medium text-gray-700 mb-1">Created</p>
                <p>{format(new Date(record.created_at), 'PPp')}</p>
              </div>
              {record.updated_at !== record.created_at && (
                <div>
                  <p className="font-medium text-gray-700 mb-1">Last Updated</p>
                  <p>{format(new Date(record.updated_at), 'PPp')}</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDelete}
        title="Delete Medical Record"
        description="Are you sure you want to delete this medical record? This will also delete any attached files."
        itemName={record?.title || ''}
        isLoading={isDeleting}
      />
    </div>
  );
}
